import type { AuthFormOutletContext } from "@v2/features/account/common";

import * as React from "react";

import { Alert } from "@replohq/lattice/blocks/alert/Alert";
import { LabeledControl } from "@replohq/lattice/blocks/labeled-control/LabeledControl";
import { Button } from "@replohq/lattice/components/button/Button";
import { Input } from "@replohq/lattice/components/input/Input";
import { Separator } from "@replohq/lattice/components/separator/Separator";
import { cn } from "@replohq/lattice/utils/cn";
import { DURATION, EASING } from "@replohq/lattice/utils/motion";
import GoogleAuth from "@v2/features/account/GoogleAuth";
import {
  hasShopifyInstallIntent,
  isAgentEnableAuthIntent,
} from "@v2/features/account/signupFunnelUtils";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import {
  useLocation,
  useOutletContext,
  useSearchParams,
} from "react-router-dom";

import { TermsAndPrivacy } from "./TermsAndPrivacy";
import { useSignupForm } from "./useSignupForm";

const SignupForm = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { pathname } = useLocation();
  const shouldReduceMotion = useReducedMotion();
  const shouldShowShopifyLoginInlineAlert =
    searchParams.get("error") === "login_in_to_associate_shopify";

  const { defaultProjectType, setDefaultProjectType } =
    useOutletContext<AuthFormOutletContext>();
  const isAgentEnable = isAgentEnableAuthIntent({ pathname, searchParams });
  const isShopifyInstall = hasShopifyInstallIntent(searchParams);
  let headerCopy: {
    title: React.ReactNode;
    description: React.ReactNode;
    isLeftAligned?: boolean;
  };
  if (isAgentEnable) {
    headerCopy = {
      title: "Welcome to Replo Agent Beta",
      description: "Create your account to turn on Agent on Replo",
    };
  } else if (defaultProjectType === "agent") {
    headerCopy = {
      title: (
        <>
          Sell{" "}
          <span className="font-editorial font-light italic">Anything</span>{" "}
          with Replo
        </>
      ),
      description: (
        <>
          Switch back to Replo&apos;s classic{" "}
          <button
            type="button"
            onClick={() => setDefaultProjectType("shopify_theme_builder")}
            className="cursor-pointer text-primary hover:underline"
          >
            Shopify Theme Builder
          </button>
        </>
      ),
      isLeftAligned: true,
    };
  } else if (isShopifyInstall) {
    // Shopify App Store installs can't switch to the Agent funnel, so no beta pitch.
    headerCopy = {
      title: "Build pages for Shopify",
      description: "Design visually. Publish to your existing theme.",
    };
  } else {
    headerCopy = {
      title: "Build directly in Shopify Themes",
      description: (
        <>
          Want to try the new version of Replo? Click here to opt-in to
          Replo&apos;s{" "}
          <button
            type="button"
            onClick={() => setDefaultProjectType("agent")}
            className="cursor-pointer text-primary hover:underline"
          >
            newest beta
          </button>
          .
        </>
      ),
      isLeftAligned: true,
    };
  }

  const {
    emailRegisterProps,
    passwordRegisterProps,
    handleSubmit,
    onSubmit,
    onGoogleSubmit,
    emailError,
    passwordError,
    isLoadingCreateAccount,
    buttonShouldBeDisabled,
    displayErrorMessage,
  } = useSignupForm({ defaultProjectType });

  const removeSearchParam = () => {
    setSearchParams((previous) => {
      const newParams = new URLSearchParams(previous);
      newParams.delete("error");
      return newParams;
    });
  };

  return (
    <form
      className="no-scrollbar"
      onSubmit={(data) => {
        if (shouldShowShopifyLoginInlineAlert) {
          removeSearchParam();
        }
        void handleSubmit(onSubmit)(data);
      }}
    >
      <div
        className={cn(
          "mb-5 flex min-h-[6.5rem] flex-col justify-center",
          headerCopy.isLeftAligned ? "items-start" : "items-center text-center",
        )}
      >
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={defaultProjectType}
            initial={shouldReduceMotion ? false : { opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={shouldReduceMotion ? { opacity: 1 } : { opacity: 0, y: -4 }}
            transition={{
              duration: shouldReduceMotion ? 0 : DURATION.standard,
              ease: EASING.outQuint,
            }}
            className={cn(
              "flex flex-col gap-4",
              headerCopy.isLeftAligned ? "items-start" : "items-center",
            )}
          >
            <h1
              className={cn(
                "font-editorial text-3xl font-bold text-default",
                headerCopy.isLeftAligned && "w-full text-left",
              )}
            >
              {headerCopy.title}
            </h1>
            <p
              className={cn(
                "text-sm text-muted",
                headerCopy.isLeftAligned
                  ? "w-full text-left"
                  : "whitespace-nowrap",
              )}
            >
              {headerCopy.description}
            </p>
          </motion.div>
        </AnimatePresence>
      </div>
      {(displayErrorMessage || shouldShowShopifyLoginInlineAlert) && (
        <Alert
          variant={
            shouldShowShopifyLoginInlineAlert && !displayErrorMessage
              ? "warning"
              : "error"
          }
          UNSAFE_className="mb-2"
          description={
            displayErrorMessage ??
            "Please create or log in to your Replo account to continue to Replo."
          }
        />
      )}
      <div className="flex flex-col gap-4">
        <LabeledControl label="Email" size="base" id="email" error={emailError}>
          <Input
            id="email"
            invalid={Boolean(emailError)}
            autoComplete="off"
            placeholder="Your Work Email"
            {...emailRegisterProps}
            type="email"
            size="base"
          />
        </LabeledControl>
        <LabeledControl
          label="Password"
          size="base"
          id="password"
          error={passwordError}
        >
          <Input
            id="password"
            invalid={Boolean(passwordError)}
            autoComplete="off"
            placeholder="••••••••••••"
            {...passwordRegisterProps}
            type="password"
            size="base"
          />
        </LabeledControl>
      </div>
      <div className="mt-10">
        <TermsAndPrivacy />
      </div>
      <Button
        className="w-full"
        UNSAFE_className="mt-6"
        variant="primary"
        size="base"
        type="submit"
        loading={isLoadingCreateAccount}
        disabled={buttonShouldBeDisabled}
      >
        Create Account
      </Button>
      <Separator className="my-4" />
      <GoogleAuth handleGoogleCredentials={onGoogleSubmit} />
    </form>
  );
};

export default SignupForm;
